import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  FileText, Plus, Send, CheckCircle, Clock, DollarSign, 
  Eye, Trash2, CreditCard, Banknote, Building2,
  Smartphone, Apple, Download, X, User, Mail, MessageSquare,
  Calendar, ChevronDown, Star, Grid3X3, Receipt, Edit2, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { MaterialCostEditor } from "@/components/MaterialCostEditor";
import { JobProfitSummary } from "@/components/JobProfitSummary";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { getSessionToken } from "@/utils/auth";
import { formatDate } from "@/utils/date";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Inline editor for a single cost field (labor / contractor payout) on a paid invoice
function CostEditor({ invoice, field, label, endpoint, testPrefix, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(invoice[field] || ""));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(String(invoice[field] || ""));
    setEditing(false);
  }, [invoice.invoice_id, invoice[field]]);

  const save = async () => {
    setSaving(true);
    try {
      const newVal = parseFloat(value) || 0;
      await axios.patch(
        `${API}/invoices/${invoice.invoice_id}/${endpoint}`,
        { [field]: newVal },
        { headers: { Authorization: `Bearer ${getSessionToken()}` } }
      );
      toast.success(`${label} updated`);
      setEditing(false);
      onSaved?.(newVal);
    } catch (e) {
      toast.error(`Failed to update ${label.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const formatted = (invoice[field] || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

  if (!editing) {
    return (
      <div className="flex items-center justify-between text-sm">
        <div className="text-green-700">
          {label}: <span className="font-semibold" data-testid={`${testPrefix}-value`}>{formatted}</span>
        </div>
        <button
          onClick={() => setEditing(true)}
          data-testid={`edit-${testPrefix}-button`}
          className="text-xs font-medium text-green-700 underline underline-offset-2 hover:text-green-900"
        >
          {invoice[field] ? "Edit" : "Add"}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-green-700 font-medium">{label}:</span>
      <div className="relative flex-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full h-9 rounded border-2 border-green-300 pl-6 pr-2 text-sm focus:border-[#207024] outline-none"
          autoFocus
          data-testid={`edit-${testPrefix}-input`}
        />
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="h-9 px-3 rounded bg-[#207024] text-white text-xs font-semibold disabled:opacity-50"
        data-testid={`save-${testPrefix}-button`}
      >
        {saving ? "..." : "Save"}
      </button>
      <button
        onClick={() => { setEditing(false); setValue(String(invoice[field] || "")); }}
        disabled={saving}
        className="h-9 px-2 rounded border border-zinc-300 text-xs text-zinc-600"
      >
        Cancel
      </button>
    </div>
  );
}

// Labor/contractor + material editors with a Job Profit summary
function JobCostsPanel({ invoice, onFieldSaved, onMaterialsSaved }) {
  const [materials, setMaterials] = useState(null);
  const isContractor = !!materials?.is_contractor;
  const field = isContractor ? "contractor_payout" : "labor_cost";
  return (
    <>
      {materials && (
        <div className="mt-3 pt-3 border-t border-green-200" data-testid={isContractor ? "contractor-payout-section" : "labor-cost-section"}>
          <CostEditor
            invoice={invoice}
            field={field}
            label={isContractor ? "Contractor Payout" : "Labor Cost"}
            endpoint={isContractor ? "contractor-payout" : "labor-cost"}
            testPrefix={isContractor ? "contractor-payout" : "labor-cost"}
            onSaved={(v) => onFieldSaved(field, v)}
          />
        </div>
      )}
      <MaterialCostEditor invoiceId={invoice.invoice_id} onLoaded={setMaterials} onSaved={onMaterialsSaved} />
      {materials && (
        <JobProfitSummary invoice={invoice} isContractor={isContractor} materials={isContractor ? 0 : materials.total} />
      )}
    </>
  );
}

// Screen-count heuristic — mirrors backend `_SCREEN_RULES` in
// routers/invoices.py. Rules evaluated in order; first match wins.
const _SCREEN_RULES = [
  { tokens: ["window", "rescreen"], weight: 1 },
  { tokens: ["new", "window"], weight: 1 },
  { tokens: ["screen door", "rescreen"], weight: 2 },
  { tokens: ["new", "screen door"], weight: 3 },
  { tokens: ["sliding", "rescreen"], weight: 2 },
  { tokens: ["new", "sliding"], weight: 4 },
];

function calculateInvoiceScreens(invoice) {
  // Manual override fully replaces the auto count when set.
  const override = invoice?.screen_count_override;
  if (typeof override === "number" && Number.isFinite(override)) {
    return override;
  }
  let total = 0;
  const items = invoice?.line_items || [];
  for (const item of items) {
    const name = (item.name || "").toLowerCase();
    const qty = item.quantity || 0;
    for (const rule of _SCREEN_RULES) {
      if (rule.tokens.every((t) => name.includes(t))) {
        total += qty * rule.weight;
        break;
      }
    }
  }
  return total;
}

// Inline editor for the per-invoice screen count with manual override.
function ScreenCountEditor({ invoice, onSaved }) {
  const autoCount = (() => {
    // Recompute the auto count ignoring any current override, so admins
    // can see what the system would say vs. what they typed.
    const { screen_count_override, ...rest } = invoice || {};
    return calculateInvoiceScreens(rest);
  })();
  const hasOverride =
    typeof invoice?.screen_count_override === "number" &&
    Number.isFinite(invoice.screen_count_override);
  const displayed = hasOverride ? invoice.screen_count_override : autoCount;

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(displayed));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(String(displayed));
    setEditing(false);
  }, [invoice?.invoice_id, invoice?.screen_count_override]);

  const save = async (overrideVal) => {
    setSaving(true);
    try {
      const token = getSessionToken();
      const body = { override: overrideVal };
      const res = await axios.patch(
        `${API}/invoices/${invoice.invoice_id}/screen-count`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        overrideVal === null ? "Reverted to auto count" : "Screen count updated"
      );
      setEditing(false);
      onSaved?.(res.data.screen_count_override ?? null);
    } catch (e) {
      toast.error("Failed to update screen count");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t pt-4" data-testid="screen-count-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-800 flex items-center gap-2">
            Screens on this invoice
            <span
              className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                hasOverride
                  ? "bg-amber-100 text-amber-800 border border-amber-200"
                  : "bg-zinc-100 text-zinc-600 border border-zinc-200"
              }`}
              data-testid="screen-count-mode-badge"
            >
              {hasOverride ? "Manual" : "Auto"}
            </span>
          </div>
          {hasOverride && (
            <div className="text-xs text-zinc-500 mt-0.5">
              Auto would count {autoCount}
            </div>
          )}
        </div>

        {!editing ? (
          <div className="flex items-center gap-2">
            <div
              className="text-2xl font-bold text-cyan-600 tabular-nums"
              data-testid="screen-count-value"
            >
              {displayed}
            </div>
            <button
              onClick={() => {
                setValue(String(displayed));
                setEditing(true);
              }}
              className="text-xs font-medium text-cyan-700 underline underline-offset-2 hover:text-cyan-900"
              data-testid="edit-screen-count-button"
            >
              Override
            </button>
            {hasOverride && (
              <button
                onClick={() => save(null)}
                disabled={saving}
                className="text-xs font-medium text-zinc-500 underline underline-offset-2 hover:text-zinc-700 disabled:opacity-50"
                data-testid="reset-screen-count-button"
              >
                Reset
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min="0"
              step="1"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-20 h-9 rounded border-2 border-cyan-300 px-2 text-sm text-right focus:border-cyan-600 outline-none"
              autoFocus
              data-testid="edit-screen-count-input"
            />
            <button
              onClick={() => {
                const n = parseInt(value, 10);
                save(Number.isFinite(n) && n >= 0 ? n : 0);
              }}
              disabled={saving}
              className="h-9 px-3 rounded bg-cyan-600 text-white text-xs font-semibold disabled:opacity-50"
              data-testid="save-screen-count-button"
            >
              {saving ? "..." : "Save"}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setValue(String(displayed));
              }}
              disabled={saving}
              className="h-9 px-2 rounded border border-zinc-300 text-xs text-zinc-600"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceStatusBadge({ invoice, StatusIcon, reconcileState }) {
  if (reconcileState === "needs") {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300 whitespace-nowrap" data-testid="invoice-status">
        <AlertTriangle className="w-3 h-3 inline mr-1" />
        Needs Reconciled
      </span>
    );
  }
  if (reconcileState === "reconciled") {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap" data-testid="invoice-status">
        <CheckCircle className="w-3 h-3 inline mr-1" />
        Paid · Reconciled
      </span>
    );
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[invoice.invoice_status]}`} data-testid="invoice-status">
      <StatusIcon className="w-3 h-3 inline mr-1" />
      {invoice.invoice_status}
    </span>
  );
}

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash", icon: Banknote },
  { value: "check", label: "Check", icon: Building2 },
  { value: "card_external", label: "Card (External)", icon: CreditCard },
  { value: "zelle", label: "Zelle", icon: Building2 },
  { value: "venmo", label: "Venmo", icon: Smartphone },
  { value: "apple_pay", label: "Apple Pay", icon: Apple },
];

// Preset line items with common services
const PRESET_LINE_ITEMS = [
  { name: "Window Rescreen", unit_price: 50, taxable: false, category: "repair" },
  { name: "New Window Screen", unit_price: 70, taxable: true, category: "new_build" },
  { name: "Sliding Door Rescreen", unit_price: 110, taxable: false, category: "repair" },
  { name: "New Sliding Door Screen", unit_price: 365, taxable: true, category: "new_build" },
  { name: "PetScreen Upgrade", unit_price: 25, taxable: true, category: "new_build" },
  { name: "Custom Service", unit_price: 0, taxable: false, category: "repair" },
];

const STATUS_COLORS = {
  draft: "bg-zinc-100 text-zinc-700",
  sent: "bg-blue-100 text-blue-700",
  paid: "bg-green-100 text-green-700",
};

const STATUS_ICONS = {
  draft: Clock,
  sent: Send,
  paid: CheckCircle,
};

// Date filter presets
const DATE_PRESETS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "custom", label: "Custom Range" },
];

// Helper to convert UTC date to PST date (for display/comparison)
const toPSTDate = (utcDate) => {
  if (!utcDate) return null;
  const date = new Date(utcDate);
  // Convert to PST by using toLocaleString with timezone
  const pstString = date.toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
  const pstDate = new Date(pstString);
  // Return date-only (midnight) for proper day comparison
  return new Date(pstDate.getFullYear(), pstDate.getMonth(), pstDate.getDate());
};

// Format a JS Date into YYYY-MM-DD using local (caller-supplied) calendar
// fields. Pairs with toPSTDate() which already returns a PST-anchored local
// midnight, so the result is the correct PST date string for the backend.
const formatYMD = (d) => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Helper to get today's date in PST
const getTodayPST = () => {
  const now = new Date();
  const pstString = now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
  const pstNow = new Date(pstString);
  return new Date(pstNow.getFullYear(), pstNow.getMonth(), pstNow.getDate());
};

// Helper to get date range for presets (in PST)
const getDateRange = (preset) => {
  const todayPST = getTodayPST();
  // End-of-day helper — the backend stats/list endpoints receive these
  // as ISO strings and do inclusive `$gte / $lte` range matches. If
  // start === end (both midnight), the range is zero-width and returns
  // 0 rows — that produced the "Today/Yesterday KPIs show 0 while list
  // has rows" bug on the Estimates dashboard. Same defect existed here.
  const endOfDay = (d) => {
    const eod = new Date(d);
    eod.setHours(23, 59, 59, 999);
    return eod;
  };

  switch (preset) {
    case "today":
      return { start: todayPST, end: endOfDay(todayPST) };
    case "yesterday": {
      const yesterday = new Date(todayPST);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: yesterday, end: endOfDay(yesterday) };
    }
    case "last7": {
      const last7Start = new Date(todayPST);
      last7Start.setDate(last7Start.getDate() - 6);
      return { start: last7Start, end: endOfDay(todayPST) };
    }
    case "thisMonth": {
      const thisMonthStart = new Date(todayPST.getFullYear(), todayPST.getMonth(), 1);
      return { start: thisMonthStart, end: endOfDay(todayPST) };
    }
    case "lastMonth": {
      const lastMonthStart = new Date(todayPST.getFullYear(), todayPST.getMonth() - 1, 1);
      const lastMonthEnd = new Date(todayPST.getFullYear(), todayPST.getMonth(), 0);
      return { start: lastMonthStart, end: endOfDay(lastMonthEnd) };
    }
    default:
      return null;
  }
};

export default function Invoices() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [needsReconcileOnly, setNeedsReconcileOnly] = useState(false);
  const [needsReconcileCount, setNeedsReconcileCount] = useState(0);
  
  // Date filtering
  const [datePreset, setDatePreset] = useState("thisMonth");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Dialog states
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [paidInvoiceId, setPaidInvoiceId] = useState(null);
  const [sendingReview, setSendingReview] = useState(false);
  const [reviewTechNames, setReviewTechNames] = useState([]);
  const [reviewProfile, setReviewProfile] = useState("main");
  const [techs, setTechs] = useState([]);
  
  // Payment form
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [laborCost, setLaborCost] = useState("");
  const [isContractorJob, setIsContractorJob] = useState(false);
  const [contractorTechId, setContractorTechId] = useState("");
  const [contractorPayout, setContractorPayout] = useState("");
  
  // Send invoice options
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSMS, setSendSMS] = useState(true);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  
  // Receipt options
  const [receiptEmail, setReceiptEmail] = useState(true);
  const [receiptSMS, setReceiptSMS] = useState(true);
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [isResendingReceipt, setIsResendingReceipt] = useState(false); // true when resending, false when after payment
  
  // Create invoice form
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // Search for invoices
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editLineItems, setEditLineItems] = useState([]);
  const [editDiscount, setEditDiscount] = useState({ type: "", value: 0, description: "" });
  const [discountPresets, setDiscountPresets] = useState([]);
  useEffect(() => {
    axios.get(`${API}/pricing-items`, { headers: { Authorization: `Bearer ${getSessionToken()}` } })
      .then((r) => {
        const order = ["Veteran Discount", "Military Discount", "Senior Discount"];
        const rank = (n) => (order.indexOf(n) === -1 ? 99 : order.indexOf(n));
        setDiscountPresets(r.data.filter((i) => i.item_type === "discount" && !i.archived).sort((a, b) => rank(a.name) - rank(b.name)));
      })
      .catch(() => {});
  }, []);
  const [editNotes, setEditNotes] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    notes: "",
    line_items: [{ name: "", quantity: 1, unit_price: 0, taxable: false }]
  });

  // Check for customer data passed from Customers page
  useEffect(() => {
    if (location.state?.createInvoice && location.state?.customer) {
      const customer = location.state.customer;
      setSelectedCustomerId(customer.customer_id || "");
      setNewInvoice(prev => ({
        ...prev,
        customer_name: customer.fullName || "",
        customer_phone: customer.phoneNumber || "",
        customer_address: customer.address || ""
      }));
      setCreateDialogOpen(true);
      // Clear the state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Handle viewing a specific invoice passed from Customers page
  useEffect(() => {
    if (location.state?.viewInvoiceId && invoices.length > 0) {
      const invoiceToView = invoices.find(i => i.invoice_id === location.state.viewInvoiceId);
      if (invoiceToView) {
        setSelectedInvoice(invoiceToView);
        setViewDialogOpen(true);
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state?.viewInvoiceId, invoices]);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      fetchInvoices();
      fetchCustomers();
      fetchTechs();
    }
  }, [user, statusFilter]);

  // Server-side stats — single source of truth, refreshes on user load and
  // whenever the date filter changes. Owning the call in ONE effect avoids
  // the double-fire that happened when both this effect and the data-load
  // effect above were firing fetchStats on initial user load.
  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user, datePreset, customDateRange]);

  const fetchTechs = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/techs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTechs(response.data || []);
    } catch (error) {
      console.error("Failed to fetch techs:", error);
    }
  };

  const isContractorInvoice = (inv) => {
    if (inv.contractor_payout !== null && inv.contractor_payout !== undefined) return true;
    const primary = (inv.completed_by_techs || [])[0];
    return !!primary && techs.some((t) => t.is_contractor && t.name === primary);
  };
  const needsReconcile = (inv) =>
    inv.invoice_status === "paid" && !isContractorInvoice(inv) && !inv.materials_reconciled_at;

  // Helper to get the relevant date for an invoice (paid_at for paid invoices, created_at otherwise)
  const getInvoiceDate = (inv) => {
    // For paid invoices, use paid_at date for stats/filtering
    if (inv.invoice_status === "paid" && inv.paid_at) {
      return toPSTDate(inv.paid_at);
    }
    // For unpaid invoices, use created_at
    return toPSTDate(inv.created_at);
  };

  // Filter invoices by date when date preset or invoices change
  useEffect(() => {
    let result = invoices;
    
    // Apply date filter first
    if (datePreset !== "all") {
      if (datePreset === "custom") {
        if (customDateRange.from && customDateRange.to) {
          result = result.filter(inv => {
            const invDate = getInvoiceDate(inv);
            if (!invDate) return false;
            invDate.setHours(0, 0, 0, 0);
            const from = new Date(customDateRange.from);
            from.setHours(0, 0, 0, 0);
            const to = new Date(customDateRange.to);
            to.setHours(23, 59, 59, 999);
            return invDate >= from && invDate <= to;
          });
        }
      } else {
        const range = getDateRange(datePreset);
        if (range) {
          result = result.filter(inv => {
            const invDate = getInvoiceDate(inv);
            if (!invDate) return false;
            invDate.setHours(0, 0, 0, 0);
            return invDate >= range.start && invDate <= range.end;
          });
        }
      }
    }
    
    setNeedsReconcileCount(result.filter(needsReconcile).length);
    if (needsReconcileOnly) result = result.filter(needsReconcile);

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(inv => 
        (inv.customer_name || "").toLowerCase().includes(query) ||
        (inv.invoice_number || "").toLowerCase().includes(query) ||
        (inv.customer_address || "").toLowerCase().includes(query) ||
        (inv.customer_phone || "").toLowerCase().includes(query)
      );
    }
    
    setFilteredInvoices(result);
  }, [datePreset, customDateRange, invoices, searchQuery, needsReconcileOnly, techs]);

  const fetchUser = async () => {
    try {
      const token = getSessionToken();
      if (!token) {
        navigate("/login");
        return;
      }
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.role !== "Admin") {
        toast.error("Access denied - Admin only");
        navigate("/jobs");
        return;
      }
      setUser(response.data);
    } catch (error) {
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const token = getSessionToken();
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setInvoices(response.data);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = getSessionToken();
      // Build date_from / date_to params matching the visible date filter.
      // Format: YYYY-MM-DD PST date strings. The server resolves PST-anchored
      // ranges for both paid_date_pst (paid invoices) and created_at (unpaid).
      const params = {};
      if (datePreset === "custom") {
        if (customDateRange.from && customDateRange.to) {
          params.date_from = formatYMD(customDateRange.from);
          params.date_to = formatYMD(customDateRange.to);
        }
      } else if (datePreset !== "all") {
        const range = getDateRange(datePreset);
        if (range) {
          params.date_from = formatYMD(range.start);
          params.date_to = formatYMD(range.end);
        }
      }
      const response = await axios.get(`${API}/invoices/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  // Handle customer selection - auto-fill fields
  const handleCustomerSelect = (customerId) => {
    setSelectedCustomerId(customerId);
    if (customerId === "") {
      // Manual entry - clear fields
      setNewInvoice(prev => ({
        ...prev,
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_address: ""
      }));
    } else {
      const customer = customers.find(c => c.customer_id === customerId);
      if (customer) {
        setNewInvoice(prev => ({
          ...prev,
          customer_name: customer.fullName || "",
          customer_email: "", // Customers don't have email stored currently
          customer_phone: customer.phoneNumber || "",
          customer_address: customer.address || ""
        }));
      }
    }
  };

  const handleSendInvoice = async () => {
    if (!sendEmail && !sendSMS) {
      toast.error("Please select at least one delivery method");
      return;
    }

    setSendingInvoice(true);
    try {
      const token = getSessionToken();
      
      // Call backend to send invoice and mark as sent
      const sendResp = await axios.post(
        `${API}/invoices/${selectedInvoice.invoice_id}/send`,
        { send_email: sendEmail, send_sms: sendSMS },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Backend now returns partial-success info: one channel may have
      // delivered, the other may have been skipped (no email/phone on file)
      // or actively failed. Surface the truth instead of always claiming
      // success — that lie was the original bug.
      const data = sendResp.data || {};
      const errors = Array.isArray(data.errors) ? data.errors : [];
      if (errors.length > 0) {
        // Partial success — show as warning toast so the admin notices.
        toast.warning(
          `Invoice sent with issues: ${errors.join("; ")}`,
          { duration: 12000 }
        );
      } else {
        toast.success("Invoice sent successfully!");
      }
      setSendDialogOpen(false);
      fetchInvoices();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invoice");
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleMarkSent = async (invoiceId) => {
    try {
      const token = getSessionToken();
      await axios.post(`${API}/invoices/${invoiceId}/mark-sent`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice marked as sent");
      fetchInvoices();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update invoice");
    }
  };

  const handleMarkPaid = async () => {
    if (!paymentMethod) {
      toast.error("Please select a payment method");
      return;
    }
    if (isContractorJob && !contractorTechId) {
      toast.error("Please select the contractor for this job");
      return;
    }
    
    try {
      const token = getSessionToken();
      const contractorTech = isContractorJob
        ? techs.find(t => t.tech_id === contractorTechId)
        : null;

      await axios.post(
        `${API}/invoices/${selectedInvoice.invoice_id}/mark-paid`,
        {
          payment_method: paymentMethod,
          payment_reference: paymentReference || null,
          labor_cost: isContractorJob ? 0 : (parseFloat(laborCost) || 0),
          contractor_payout: isContractorJob
            ? (parseFloat(contractorPayout) || 0)
            : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If this was a contractor job, also stamp them as the primary
      // tech so Tech Performance credits + KPIs stay in sync.
      if (isContractorJob && contractorTech) {
        try {
          await axios.put(
            `${API}/invoices/${selectedInvoice.invoice_id}/completed-by`,
            { tech_names: [contractorTech.name] },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch {
          // Non-fatal — admin can fix from the Tech Performance page.
        }
      }
      
      toast.success(`Invoice ${selectedInvoice.invoice_number} marked as paid!`);
      
      // Store invoice ID for receipt/review
      const invoiceId = selectedInvoice.invoice_id;
      setPayDialogOpen(false);
      setPaymentMethod("");
      setPaymentReference("");
      setLaborCost("");
      setIsContractorJob(false);
      setContractorTechId("");
      setContractorPayout("");
      
      // Show receipt dialog to let user choose how to send
      setPaidInvoiceId(invoiceId);
      setReceiptEmail(true);
      setReceiptSMS(true);
      setIsResendingReceipt(false); // This is a new payment, not a resend
      setReceiptDialogOpen(true);
      
      fetchInvoices();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as paid");
    }
  };

  const handleSendReceipt = async (sendReceipt) => {
    if (!sendReceipt) {
      // Skip receipt
      setReceiptDialogOpen(false);
      if (!isResendingReceipt) {
        // Only show review dialog if this was after marking paid
        setReviewDialogOpen(true);
      }
      return;
    }
    
    if (!receiptEmail && !receiptSMS) {
      toast.error("Please select at least one delivery method");
      return;
    }
    
    setSendingReceipt(true);
    try {
      const token = getSessionToken();
      await axios.post(
        `${API}/invoices/${paidInvoiceId}/send-receipt`,
        { send_email: receiptEmail, send_sms: receiptSMS },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const methods = [];
      if (receiptSMS) methods.push("text");
      if (receiptEmail) methods.push("email");
      toast.success(`Receipt sent via ${methods.join(" and ")}!`);
      
      setReceiptDialogOpen(false);
      if (!isResendingReceipt) {
        // Only show review dialog if this was after marking paid
        setReviewDialogOpen(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send receipt");
    } finally {
      setSendingReceipt(false);
    }
  };
  
  // Open receipt dialog for resending
  const handleResendReceipt = (invoice) => {
    setPaidInvoiceId(invoice.invoice_id);
    setReceiptEmail(true);
    setReceiptSMS(true);
    setIsResendingReceipt(true);
    setReceiptDialogOpen(true);
  };

  const handleSendReview = async (sendReview) => {
    if (!sendReview) {
      setReviewDialogOpen(false);
      setPaidInvoiceId(null);
      setReviewTechNames([]);
      setReviewProfile("main");
      return;
    }
    
    setSendingReview(true);
    try {
      const token = getSessionToken();
      const response = await axios.post(
        `${API}/invoices/${paidInvoiceId}/send-review`,
        { 
          send_review: true,
          tech_names: reviewTechNames.length > 0 ? reviewTechNames : null,
          profile: reviewProfile
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Review request sent! Tech: ${response.data.tech_name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send review request");
    } finally {
      setSendingReview(false);
      setReviewDialogOpen(false);
      setPaidInvoiceId(null);
      setReviewTechNames([]);
      setReviewProfile("main");
    }
  };

  const handleDelete = async (force = false) => {
    try {
      const token = getSessionToken();
      const url = force 
        ? `${API}/invoices/${selectedInvoice.invoice_id}?force=true`
        : `${API}/invoices/${selectedInvoice.invoice_id}`;
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(force ? "Invoice force deleted" : "Invoice deleted");
      setDeleteDialogOpen(false);
      setSelectedInvoice(null);
      fetchInvoices();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete invoice");
    }
  };

  // Export invoices to CSV
  const handleExportCSV = () => {
    if (filteredInvoices.length === 0) {
      toast.error("No invoices to export");
      return;
    }

    const headers = [
      "Invoice #", "Status", "Customer Name", "Customer Email", "Customer Phone",
      "Address", "Subtotal", "Taxable Amount", "Tax", "Total",
      "Payment Method", "Payment Reference", "Created Date", "Paid Date"
    ];

    const rows = filteredInvoices.map(inv => [
      inv.invoice_number,
      inv.invoice_status,
      inv.customer_name || "",
      inv.customer_email || "",
      inv.customer_phone || "",
      (inv.customer_address || "").replace(/,/g, ";"),
      inv.subtotal?.toFixed(2) || "0.00",
      inv.taxable_subtotal?.toFixed(2) || "0.00",
      inv.tax?.toFixed(2) || "0.00",
      inv.total?.toFixed(2) || "0.00",
      inv.payment_method || "",
      inv.payment_reference || "",
      inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "",
      inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const filterLabel = statusFilter ? `_${statusFilter}` : "";
    const dateLabel = datePreset !== "all" ? `_${datePreset}` : "";
    link.download = `screenzz_invoices${filterLabel}${dateLabel}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredInvoices.length} invoices to CSV`);
  };

  // Create new invoice manually
  const handleCreateInvoice = async () => {
    if (!newInvoice.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    
    const validLineItems = newInvoice.line_items.filter(item => 
      item.name.trim() && item.quantity > 0 && item.unit_price > 0
    );
    
    if (validLineItems.length === 0) {
      toast.error("At least one valid line item is required");
      return;
    }

    try {
      const token = getSessionToken();
      const lineItemsFormatted = validLineItems.map(item => ({
        name: item.name.trim(),
        quantity: parseInt(item.quantity),
        unit_price: parseFloat(item.unit_price),
        line_total: parseFloat(item.unit_price) * parseInt(item.quantity),
        category: item.taxable ? "new_build" : "repair",
        taxable: item.taxable
      }));

      await axios.post(`${API}/invoices`, {
        customer_name: newInvoice.customer_name.trim(),
        customer_email: newInvoice.customer_email.trim() || null,
        customer_phone: newInvoice.customer_phone.trim() || null,
        customer_address: newInvoice.customer_address.trim() || null,
        customer_id: selectedCustomerId || null,
        notes: newInvoice.notes.trim() || null,
        line_items: lineItemsFormatted,
        tax_rate: 0.0875
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Invoice created");
      setCreateDialogOpen(false);
      setSelectedCustomerId("");
      setNewInvoice({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_address: "",
        notes: "",
        line_items: [{ name: "", quantity: 1, unit_price: 0, taxable: false }]
      });
      fetchInvoices();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    }
  };

  // Add line item to new invoice
  const addLineItem = () => {
    setNewInvoice(prev => ({
      ...prev,
      line_items: [...prev.line_items, { name: "", quantity: 1, unit_price: 0, taxable: false }]
    }));
  };

  // Add preset line item
  const addPresetLineItem = (preset) => {
    setNewInvoice(prev => ({
      ...prev,
      line_items: [...prev.line_items, { 
        name: preset.name, 
        quantity: 1, 
        unit_price: preset.unit_price, 
        taxable: preset.taxable 
      }]
    }));
  };

  // Remove line item from new invoice
  const removeLineItem = (index) => {
    if (newInvoice.line_items.length <= 1) return;
    setNewInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  // Update line item in new invoice
  const updateLineItem = (index, field, value) => {
    setNewInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Handle preset selection for a line item
  const handlePresetSelect = (index, presetName) => {
    const preset = PRESET_LINE_ITEMS.find(p => p.name === presetName);
    if (preset) {
      setNewInvoice(prev => ({
        ...prev,
        line_items: prev.line_items.map((item, i) => 
          i === index ? { 
            name: preset.name, 
            quantity: item.quantity || 1, 
            unit_price: preset.unit_price, 
            taxable: preset.taxable 
          } : item
        )
      }));
    }
  };

  // Calculate new invoice totals
  const calculateNewInvoiceTotals = () => {
    const validItems = newInvoice.line_items.filter(item => 
      item.name.trim() && item.quantity > 0 && item.unit_price > 0
    );
    const subtotal = validItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const taxableSubtotal = validItems.filter(item => item.taxable).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax = taxableSubtotal * 0.0875;
    const total = subtotal + tax;
    return { subtotal, taxableSubtotal, tax, total };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const openEditDialog = (invoice) => {
    setEditInvoice(invoice);
    setEditLineItems(invoice.line_items?.map(item => ({
      name: item.name || "",
      quantity: item.quantity || 1,
      unit_price: item.unit_price || 0,
      taxable: item.taxable || false
    })) || [{ name: "", quantity: 1, unit_price: 0, taxable: false }]);
    setEditDiscount({
      type: invoice.discount_type || "",
      value: invoice.discount_value || 0,
      description: invoice.discount_description || ""
    });
    setEditNotes(invoice.notes || "");
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    const validItems = editLineItems.filter(i => i.name.trim() && i.quantity > 0 && i.unit_price > 0);
    if (validItems.length === 0) {
      toast.error("At least one valid line item is required");
      return;
    }
    setSavingEdit(true);
    try {
      const token = getSessionToken();
      const payload = {
        line_items: validItems,
        notes: editNotes || null
      };
      if (editDiscount.type && editDiscount.value > 0) {
        payload.discount_type = editDiscount.type;
        payload.discount_value = editDiscount.value;
        payload.discount_description = editDiscount.description || null;
      } else {
        payload.discount_type = "none";
        payload.discount_value = 0;
        payload.discount_description = null;
      }
      await axios.put(`${API}/invoices/${editInvoice.invoice_id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice updated");
      setEditDialogOpen(false);
      setEditInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update invoice");
    } finally {
      setSavingEdit(false);
    }
  };



  // Total screens across a set of invoices. Delegates to
  // `calculateInvoiceScreens` so per-invoice manual overrides are honored.
  const calculateTotalScreens = (invoices) => {
    let totalScreens = 0;
    for (const inv of invoices) {
      totalScreens += calculateInvoiceScreens(inv);
    }
    return totalScreens;
  };

  // Calculate filtered stats based on current date filter.
  //
  // Strategy: server-side `stats` is the source of truth (no row cap), so
  // when the user is NOT searching, we surface server numbers directly.
  // When the user is searching, we fall back to a client-side reduce over
  // `filteredInvoices` so the KPI panel reflects what they typed —
  // acceptable trade-off since search zooms into a specific customer and
  // is bounded by the (capped) list anyway.
  const getFilteredStats = () => {
    if (stats && !searchQuery.trim()) {
      return stats;
    }

    const draft = filteredInvoices.filter(inv => inv.invoice_status === "draft");
    const sent = filteredInvoices.filter(inv => inv.invoice_status === "sent");
    const paid = filteredInvoices.filter(inv => inv.invoice_status === "paid");
    
    const pendingTotal = [...draft, ...sent].reduce((sum, inv) => sum + (inv.total || 0), 0);
    const collectedTotal = paid.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const laborTotal = paid.reduce((sum, inv) => sum + (inv.labor_cost || 0), 0);
    const netTotal = collectedTotal - laborTotal;
    const netMarginPct = collectedTotal > 0 ? (netTotal / collectedTotal) * 100 : 0;
    const taxCollected = paid.reduce((sum, inv) => sum + (inv.tax || 0), 0);
    const totalScreens = calculateTotalScreens(paid);
    const jobCount = paid.length;
    const screensPerJob = jobCount > 0 ? totalScreens / jobCount : 0;
    const avgJobSize = jobCount > 0 ? collectedTotal / jobCount : 0;
    
    return {
      counts: {
        draft: draft.length,
        sent: sent.length,
        paid: paid.length,
        total: filteredInvoices.length
      },
      pendingTotal,
      collectedTotal,
      laborTotal,
      netTotal,
      netMarginPct,
      taxCollected,
      totalScreens,
      jobCount,
      screensPerJob,
      avgJobSize
    };
  };

  const filteredStats = getFilteredStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <Header user={user} />
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-barlow text-2xl font-bold text-zinc-900 uppercase">
              Invoices
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5" data-testid="invoices-count">
              {filteredStats.counts.total} {filteredStats.counts.total === 1 ? "invoice" : "invoices"} • {filteredStats.counts.paid} paid
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportCSV}
              data-testid="export-csv-button"
              className="px-3 py-2 text-sm bg-white border text-zinc-700 rounded-lg hover:bg-zinc-50 flex items-center gap-1"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={() => setCreateDialogOpen(true)}
              data-testid="create-invoice-button"
              className="px-3 py-2 text-sm bg-[#207024] text-white rounded-lg hover:bg-[#1a5c1d] flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              New Invoice
            </button>
          </div>
        </div>

        {/* Mini Dashboard - Adjusts to Date Filter */}
        <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-xl p-4 mb-4 text-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-zinc-400">
              {datePreset === "all" ? "All Time" : 
               datePreset === "custom" && customDateRange.from && customDateRange.to
                 ? `${customDateRange.from.toLocaleDateString()} - ${customDateRange.to.toLocaleDateString()}`
                 : DATE_PRESETS.find(p => p.value === datePreset)?.label}
            </span>
            <span className="text-xs text-zinc-500">{filteredStats.counts.total} invoices</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-500/20 rounded-lg p-3 border border-green-500/30">
              <div className="text-2xl font-bold text-green-400">{formatCurrency(filteredStats.collectedTotal)}</div>
              <div className="text-xs text-green-300/80 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Collected ({filteredStats.counts.paid})
              </div>
            </div>
            <div className="bg-amber-500/20 rounded-lg p-3 border border-amber-500/30">
              <div className="text-2xl font-bold text-amber-400">{formatCurrency(filteredStats.pendingTotal)}</div>
              <div className="text-xs text-amber-300/80 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Pending ({filteredStats.counts.draft + filteredStats.counts.sent})
              </div>
            </div>
          </div>
          {/* Additional Stats Row - Jobs, Screens, Screens/Job, Avg Job */}
          <div className="mt-3 pt-3 border-t border-zinc-700 grid grid-cols-4 gap-3 text-sm">
            <div className="text-center">
              <div className="text-lg font-bold text-white">{filteredStats.jobCount}</div>
              <div className="text-xs text-zinc-400">Jobs</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-cyan-400">{filteredStats.totalScreens.toLocaleString()}</div>
              <div className="text-xs text-zinc-400">Screens</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-purple-400">{filteredStats.screensPerJob.toFixed(1)}</div>
              <div className="text-xs text-zinc-400">Screens/Job</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-400">{formatCurrency(filteredStats.avgJobSize)}</div>
              <div className="text-xs text-zinc-400">Avg Job Size</div>
            </div>
          </div>
        </div>

        {/* Search + Date Filter (75/25) + status quick-filters */}
        <div className="flex gap-2 mb-3">
          <div className="relative" style={{ flex: "0 0 75%" }}>
            <input
              type="text"
              placeholder="Search by name, invoice #, address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 pl-10 pr-8 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#207024]/20 focus:border-[#207024]"
              data-testid="invoice-search-input"
            />
            <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div style={{ flex: "0 0 calc(25% - 0.5rem)" }} className="min-w-0">
            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
              <PopoverTrigger asChild>
                <button
                  className={`w-full px-2 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 ${
                    datePreset !== "all"
                      ? "bg-blue-100 text-blue-700 border border-blue-200"
                      : "bg-white border border-zinc-200 text-zinc-600"
                  }`}
                  data-testid="invoice-date-filter-button"
                >
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate text-xs">
                    {datePreset === "custom" && customDateRange.from && customDateRange.to
                      ? `${customDateRange.from.toLocaleDateString()}`
                      : DATE_PRESETS.find(p => p.value === datePreset)?.label || "All Time"
                    }
                  </span>
                  <ChevronDown className="w-3 h-3 flex-shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 overflow-y-auto"
                style={{
                  // Radix populates this with the actual space available
                  // between the trigger and viewport edge (accounts for iOS
                  // Safari URL bar, keyboard, etc). Combined with overflow-y
                  // it makes the popover scroll internally instead of clipping.
                  maxHeight: "var(--radix-popover-content-available-height)",
                }}
                align="end"
                side="bottom"
                sideOffset={6}
                collisionPadding={12}
                avoidCollisions
              >
                <div className="p-2 border-b">
                  <div className="grid grid-cols-2 gap-1">
                    {DATE_PRESETS.filter(p => p.value !== "custom").map((preset) => (
                      <button
                        key={preset.value}
                        onClick={() => {
                          setDatePreset(preset.value);
                          if (preset.value !== "custom") {
                            setShowDatePicker(false);
                          }
                        }}
                        className={`px-3 py-2 text-sm rounded-lg text-left ${
                          datePreset === preset.value
                            ? "bg-[#207024] text-white"
                            : "hover:bg-zinc-100"
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-sm font-medium text-zinc-700 mb-2">Custom Range</div>
                  <CalendarComponent
                    mode="range"
                    selected={customDateRange}
                    onSelect={(range) => {
                      setCustomDateRange(range || { from: null, to: null });
                      if (range?.from && range?.to) {
                        setDatePreset("custom");
                      }
                    }}
                    numberOfMonths={1}
                    className="rounded-md border"
                  />
                  {customDateRange.from && customDateRange.to && (
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="mt-2 w-full py-2 bg-[#207024] text-white rounded-lg text-sm font-medium"
                    >
                      Apply Range
                    </button>
                  )}
                  {datePreset !== "all" && (
                    <button
                      onClick={() => {
                        setDatePreset("all");
                        setCustomDateRange({ from: null, to: null });
                        setShowDatePicker(false);
                      }}
                      className="mt-2 w-full py-2 text-sm text-zinc-500 hover:text-zinc-700 border border-zinc-200 rounded-lg"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Status quick-filter pills */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          <button
            onClick={() => { setNeedsReconcileOnly(false); setStatusFilter(null); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              statusFilter === null
                ? "bg-zinc-800 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
            data-testid="filter-all"
          >
            All ({filteredStats.counts.total})
          </button>
          <button
            onClick={() => { setNeedsReconcileOnly(false); setStatusFilter(statusFilter === "draft" ? null : "draft"); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "draft"
                ? "bg-zinc-700 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
            data-testid="filter-draft"
          >
            Draft ({filteredStats.counts.draft})
          </button>
          <button
            onClick={() => { setNeedsReconcileOnly(false); setStatusFilter(statusFilter === "sent" ? null : "sent"); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "sent"
                ? "bg-blue-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
            data-testid="filter-sent"
          >
            Sent ({filteredStats.counts.sent})
          </button>
          <button
            onClick={() => { setNeedsReconcileOnly(false); setStatusFilter(statusFilter === "paid" ? null : "paid"); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "paid"
                ? "bg-green-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
            data-testid="filter-paid"
          >
            Paid ({filteredStats.counts.paid})
          </button>
          <button
            onClick={() => { setStatusFilter(null); setNeedsReconcileOnly(!needsReconcileOnly); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              needsReconcileOnly
                ? "bg-amber-500 text-white"
                : "bg-white border border-amber-300 text-amber-700 hover:bg-amber-50"
            }`}
            data-testid="filter-needs-reconcile"
          >
            Needs Reconciled ({needsReconcileCount})
          </button>
        </div>

        {/* Invoice List */}
        <div className="space-y-3" data-testid="invoices-list">
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border" data-testid="no-invoices-message">
              <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500">
                {invoices.length === 0 ? "No invoices yet" : "No invoices in this date range"}
              </p>
              <p className="text-sm text-zinc-400 mt-1">
                {invoices.length === 0 ? 'Click "New Invoice" to create one' : "Try a different date filter"}
              </p>
            </div>
          ) : (
            filteredInvoices.map((invoice) => {
              const StatusIcon = STATUS_ICONS[invoice.invoice_status];
              return (
                <div
                  key={invoice.invoice_id}
                  data-testid={`invoice-card-${invoice.invoice_id}`}
                  className="bg-white rounded-xl border p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-zinc-900" data-testid="invoice-customer">{invoice.customer_name}</span>
                        <InvoiceStatusBadge
                          invoice={invoice}
                          StatusIcon={StatusIcon}
                          reconcileState={
                            invoice.invoice_status !== "paid" || isContractorInvoice(invoice)
                              ? null
                              : invoice.materials_reconciled_at ? "reconciled" : "needs"
                          }
                        />
                      </div>
                      {invoice.customer_address && invoice.customer_address !== "Not provided yet" && (
                        <div className="text-sm text-zinc-600 mt-0.5" data-testid="invoice-address">{invoice.customer_address}</div>
                      )}
                      <div className="text-xs text-zinc-400 mt-1" data-testid="invoice-number">
                        {/* Show only the invoice number portion, not the old "NAME - ADDRESS" suffix */}
                        {invoice.invoice_number?.split(" - ")[0] || invoice.invoice_number}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg text-zinc-900" data-testid="invoice-total">{formatCurrency(invoice.total)}</div>
                      <div className="text-xs text-zinc-500">{formatDate(invoice.created_at)}</div>
                    </div>
                  </div>

                  {/* Payment info for paid invoices */}
                  {invoice.invoice_status === "paid" && (
                    <div className="bg-green-50 rounded-lg p-2 mb-3 text-sm" data-testid="payment-info">
                      <span className="text-green-700">
                        Paid via {PAYMENT_METHODS.find(m => m.value === invoice.payment_method)?.label || invoice.payment_method}
                        {invoice.payment_reference && ` • Ref: ${invoice.payment_reference}`}
                        {invoice.paid_at && ` • ${formatDate(invoice.paid_at)}`}
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setViewDialogOpen(true);
                      }}
                      data-testid="view-invoice-button"
                      className="px-3 py-1.5 text-sm bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200"
                    >
                      <Eye className="w-4 h-4 inline mr-1" />
                      View
                    </button>
                    
                    {invoice.invoice_status === "draft" && (
                      <>
                        <button
                          onClick={() => openEditDialog(invoice)}
                          data-testid="edit-invoice-button"
                          className="px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200"
                        >
                          <Edit2 className="w-4 h-4 inline mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            // Default BOTH channels on when opening. Backend
                            // resolves the customer's phone/email from the
                            // linked customer doc if the invoice snapshot
                            // itself is missing them, so we shouldn't gate
                            // the checkbox purely on invoice fields (that
                            // caused SMS to silently never get sent).
                            setSendEmail(true);
                            setSendSMS(true);
                            setSendDialogOpen(true);
                          }}
                          data-testid="send-invoice-button"
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                        >
                          <Send className="w-4 h-4 inline mr-1" />
                          Send Invoice
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid="delete-invoice-button"
                          className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                        >
                          <Trash2 className="w-4 h-4 inline mr-1" />
                          Delete
                        </button>
                      </>
                    )}

                    {invoice.invoice_status === "sent" && (
                      <>
                        <button
                          onClick={() => openEditDialog(invoice)}
                          data-testid="edit-sent-invoice-button"
                          className="px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200"
                        >
                          <Edit2 className="w-4 h-4 inline mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setSendEmail(true);
                            setSendSMS(true);
                            setSendDialogOpen(true);
                          }}
                          data-testid="resend-invoice-button"
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                        >
                          <Send className="w-4 h-4 inline mr-1" />
                          Resend
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid="delete-sent-invoice-button"
                          className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                        >
                          <Trash2 className="w-4 h-4 inline mr-1" />
                          Delete
                        </button>
                      </>
                    )}
                    
                    {(invoice.invoice_status === "draft" || invoice.invoice_status === "sent") && (
                      <button
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setPayDialogOpen(true);
                        }}
                        data-testid="mark-paid-button"
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                      >
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Mark Paid
                      </button>
                    )}

                    {/* Resend Receipt for paid invoices */}
                    {invoice.invoice_status === "paid" && (
                      <button
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setPaidInvoiceId(invoice.invoice_id);
                          setReceiptEmail(!!invoice.customer_email);
                          setReceiptSMS(!!invoice.customer_phone);
                          setIsResendingReceipt(true);
                          setReceiptDialogOpen(true);
                        }}
                        data-testid="resend-receipt-button"
                        className="px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                      >
                        <Receipt className="w-4 h-4 inline mr-1" />
                        Resend Receipt
                      </button>
                    )}

                    {/* Force delete for paid invoices (testing) */}
                    {invoice.invoice_status === "paid" && (
                      <button
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid="delete-paid-invoice-button"
                        className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                      >
                        <Trash2 className="w-4 h-4 inline mr-1" />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* View Invoice Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice {selectedInvoice?.invoice_number}</DialogTitle>
            <DialogDescription className="sr-only">View invoice details</DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-semibold text-zinc-900">{selectedInvoice.customer_name}</div>
                  {selectedInvoice.customer_email && (
                    <div className="text-sm text-zinc-500">{selectedInvoice.customer_email}</div>
                  )}
                  {selectedInvoice.customer_phone && (
                    <div className="text-sm text-zinc-500">{selectedInvoice.customer_phone}</div>
                  )}
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[selectedInvoice.invoice_status]}`}>
                  {selectedInvoice.invoice_status}
                </span>
              </div>

              {selectedInvoice.customer_address && (
                <div className="text-sm text-zinc-600 bg-zinc-50 p-3 rounded-lg">
                  {selectedInvoice.customer_address}
                </div>
              )}

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Line Items</h4>
                <div className="space-y-2">
                  {selectedInvoice.line_items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>
                        {item.name} × {item.quantity}
                        {item.quantity > 1 && item.unit_price > 0 && (
                          <span className="text-zinc-400 ml-1">(${item.unit_price.toFixed(2)}/ea)</span>
                        )}
                        {item.taxable && <span className="text-zinc-400 ml-1">(taxable)</span>}
                      </span>
                      <span className="font-medium">{formatCurrency(item.line_total)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Editable per-invoice screen count with manual override */}
              <ScreenCountEditor
                invoice={selectedInvoice}
                onSaved={(newOverride) => {
                  setSelectedInvoice({
                    ...selectedInvoice,
                    screen_count_override: newOverride,
                  });
                  fetchInvoices();
                }}
              />

              <div className="border-t pt-4 space-y-2">
                {/* Show original subtotal and discount if applicable */}
                {selectedInvoice.discount_amount > 0 ? (
                  <>
                    <div className="flex justify-between text-sm text-zinc-500">
                      <span>Original Subtotal</span>
                      <span>{formatCurrency(selectedInvoice.subtotal_before_discount || (selectedInvoice.subtotal + selectedInvoice.discount_amount))}</span>
                    </div>
                    <div className="flex justify-between text-sm text-green-600">
                      <span>
                        Discount
                        {selectedInvoice.discount_type === "percent" && selectedInvoice.discount_value && (
                          <span className="text-green-500 ml-1">({selectedInvoice.discount_value}%)</span>
                        )}
                        {selectedInvoice.discount_description && (
                          <span className="text-green-500 ml-1">— {selectedInvoice.discount_description}</span>
                        )}
                      </span>
                      <span>-{formatCurrency(selectedInvoice.discount_amount)}</span>
                    </div>
                  </>
                ) : (
                  /* Detect hidden discount: if line items total != subtotal, there's an unapplied discount */
                  (() => {
                    const lineItemsTotal = selectedInvoice.line_items?.reduce((sum, item) => 
                      sum + ((item.quantity || 1) * (item.unit_price || 0)), 0) || 0;
                    const hiddenDiscount = lineItemsTotal - selectedInvoice.subtotal;
                    if (hiddenDiscount > 0.01) {
                      const discountPercent = Math.round((hiddenDiscount / lineItemsTotal) * 100);
                      return (
                        <>
                          <div className="flex justify-between text-sm text-zinc-500">
                            <span>Line Items Total</span>
                            <span>{formatCurrency(lineItemsTotal)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-green-600">
                            <span>Discount ({discountPercent}%)</span>
                            <span>-{formatCurrency(hiddenDiscount)}</span>
                          </div>
                        </>
                      );
                    }
                    return null;
                  })()
                )}
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                </div>
                {/* Only show taxable amount if it's different from subtotal and makes sense */}
                {selectedInvoice.tax > 0 && (
                  <div className="flex justify-between text-sm text-zinc-500">
                    <span>Taxable Amount</span>
                    <span>{formatCurrency(Math.round(selectedInvoice.tax / selectedInvoice.tax_rate * 100) / 100)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span>Tax ({(selectedInvoice.tax_rate * 100).toFixed(2)}%)</span>
                  <span>{formatCurrency(selectedInvoice.tax)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(selectedInvoice.total)}</span>
                </div>
              </div>

              {selectedInvoice.invoice_status === "paid" && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-green-800 mb-2">Payment Details</h4>
                      <div className="text-sm text-green-700 space-y-1">
                        <div>Method: {PAYMENT_METHODS.find(m => m.value === selectedInvoice.payment_method)?.label}</div>
                        {selectedInvoice.payment_reference && (
                          <div>Reference: {selectedInvoice.payment_reference}</div>
                        )}
                        <div>Paid: {formatDate(selectedInvoice.paid_at)}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setViewDialogOpen(false);
                        handleResendReceipt(selectedInvoice);
                      }}
                      className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                    >
                      <Receipt className="w-4 h-4" />
                      Resend Receipt
                    </button>
                  </div>

                  <JobCostsPanel
                    key={selectedInvoice.invoice_id}
                    invoice={selectedInvoice}
                    onFieldSaved={(field, newVal) => {
                      setSelectedInvoice({ ...selectedInvoice, [field]: newVal });
                      fetchInvoices();
                    }}
                    onMaterialsSaved={fetchInvoices}
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Invoice Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Invoice</DialogTitle>
            <DialogDescription>Choose how to send invoice {selectedInvoice?.invoice_number}</DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              <div className="bg-zinc-50 rounded-lg p-4">
                <div className="font-bold text-lg">{selectedInvoice.invoice_number}</div>
                <div className="text-zinc-600">{selectedInvoice.customer_name}</div>
                <div className="text-xl font-bold text-[#207024] mt-2">
                  {formatCurrency(selectedInvoice.total)}
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    disabled={!selectedInvoice.customer_email}
                    className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                  />
                  <Mail className="w-5 h-5 text-zinc-500" />
                  <div className="flex-1">
                    <div className="font-medium">Email</div>
                    <div className="text-sm text-zinc-500">
                      {selectedInvoice.customer_email || "No email on file"}
                    </div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                  <input
                    type="checkbox"
                    checked={sendSMS}
                    onChange={(e) => setSendSMS(e.target.checked)}
                    disabled={!selectedInvoice.customer_phone}
                    className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                  />
                  <MessageSquare className="w-5 h-5 text-zinc-500" />
                  <div className="flex-1">
                    <div className="font-medium">Text Message (SMS)</div>
                    <div className="text-sm text-zinc-500">
                      {selectedInvoice.customer_phone || "No phone on file"}
                    </div>
                  </div>
                </label>
              </div>

              {!selectedInvoice.customer_email && !selectedInvoice.customer_phone && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                  No contact info available. Add email or phone to send invoice.
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setSendDialogOpen(false)}
                  className="flex-1 py-3 border-2 border-zinc-200 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendInvoice}
                  disabled={sendingInvoice || (!sendEmail && !sendSMS)}
                  className="flex-1 py-3 bg-[#207024] text-white rounded-lg font-semibold disabled:opacity-50"
                >
                  {sendingInvoice ? "Sending..." : "Send Invoice"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Invoice as Paid</DialogTitle>
            <DialogDescription className="sr-only">Select payment method</DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              <div className="bg-zinc-50 rounded-lg p-4">
                <div className="text-sm text-zinc-500">Invoice</div>
                <div className="font-bold text-lg">{selectedInvoice.invoice_number}</div>
                <div className="text-zinc-600">{selectedInvoice.customer_name}</div>
                <div className="text-xl font-bold text-[#207024] mt-2">
                  {formatCurrency(selectedInvoice.total)}
                </div>
              </div>

              <div>
                <label className="block font-semibold text-zinc-700 mb-3">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map((method) => {
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.value}
                        onClick={() => setPaymentMethod(method.value)}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                          paymentMethod === method.value
                            ? "border-[#207024] bg-green-50"
                            : "border-zinc-200 hover:border-zinc-300"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{method.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block font-semibold text-zinc-700 mb-2">
                  Reference Number (optional)
                </label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Check #, Transaction ID, etc."
                  className="w-full h-12 rounded-lg border-2 border-zinc-200 px-4 focus:border-[#207024]"
                />
              </div>

              {/* Contractor toggle — swaps in a flat payout in place
                  of the material auto-calc + labor cost. */}
              <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-zinc-200 cursor-pointer hover:border-orange-400 transition-colors">
                <input
                  type="checkbox"
                  data-testid="contractor-job-checkbox"
                  checked={isContractorJob}
                  onChange={(e) => setIsContractorJob(e.target.checked)}
                  className="w-5 h-5 rounded border-zinc-300 text-orange-600 focus:ring-orange-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Contractor job — flat payout</div>
                  <div className="text-xs text-zinc-500">
                    Skip auto material + labor. Use one flat payout to the subcontractor.
                  </div>
                </div>
              </label>

              {isContractorJob ? (
                <div className="rounded-lg border-2 border-orange-200 bg-orange-50 p-3 space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-orange-900 mb-1">
                      Contractor
                    </label>
                    <select
                      data-testid="contractor-tech-select"
                      value={contractorTechId}
                      onChange={(e) => setContractorTechId(e.target.value)}
                      className="w-full h-11 rounded-lg border-2 border-orange-200 bg-white px-3 focus:border-orange-500"
                    >
                      <option value="">Select a contractor…</option>
                      {techs.filter(t => t.is_contractor).map(t => (
                        <option key={t.tech_id} value={t.tech_id}>{t.name}</option>
                      ))}
                    </select>
                    {techs.filter(t => t.is_contractor).length === 0 && (
                      <p className="text-xs text-orange-800 mt-1">
                        No contractors yet — mark a tech as Contractor from Settings → Techs.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-orange-900 mb-1">
                      Contractor Payout
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={contractorPayout}
                        onChange={(e) => setContractorPayout(e.target.value)}
                        placeholder="0.00"
                        data-testid="contractor-payout-input"
                        className="w-full h-11 rounded-lg border-2 border-orange-200 bg-white pl-7 pr-3 focus:border-orange-500"
                      />
                    </div>
                    <p className="text-xs text-orange-800 mt-1">
                      Total amount you&apos;re paying the contractor for this job.
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block font-semibold text-zinc-700 mb-2">
                    Labor Cost (optional)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={laborCost}
                      onChange={(e) => setLaborCost(e.target.value)}
                      placeholder="0.00"
                      data-testid="labor-cost-input"
                      className="w-full h-12 rounded-lg border-2 border-zinc-200 pl-8 pr-4 focus:border-[#207024]"
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    What you paid your tech for this job. Used in your Net Revenue calc.
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setPayDialogOpen(false);
                    setPaymentMethod("");
                    setPaymentReference("");
                    setLaborCost("");
                    setIsContractorJob(false);
                    setContractorTechId("");
                    setContractorPayout("");
                  }}
                  className="flex-1 py-3 border-2 border-zinc-200 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkPaid}
                  disabled={!paymentMethod}
                  className="flex-1 py-3 bg-[#207024] text-white rounded-lg font-semibold disabled:opacity-50"
                >
                  <CheckCircle className="w-5 h-5 inline mr-2" />
                  Mark as Paid
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedInvoice?.invoice_status === "paid" ? (
                <span className="text-amber-600 font-medium">
                  ⚠️ This is a PAID invoice. Deleting paid invoices affects financial records. Are you sure?
                </span>
              ) : (
                <>This will permanently delete invoice {selectedInvoice?.invoice_number}. This action cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(selectedInvoice?.invoice_status === "paid")} 
              className="bg-red-600 hover:bg-red-700"
            >
              {selectedInvoice?.invoice_status === "paid" ? "Force Delete" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Invoice Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Invoice</DialogTitle>
            <DialogDescription className="sr-only">Create a new invoice</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Customer Selector */}
            <div>
              <label className="block font-semibold text-zinc-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Select Customer
              </label>
              <select
                value={selectedCustomerId}
                onChange={(e) => handleCustomerSelect(e.target.value)}
                data-testid="customer-selector"
                className="w-full h-11 rounded-lg border-2 border-zinc-200 px-3 focus:border-[#207024]"
              >
                <option value="">-- Enter manually --</option>
                {customers.map(customer => (
                  <option key={customer.customer_id} value={customer.customer_id}>
                    {customer.fullName} - {customer.phoneNumber}
                  </option>
                ))}
              </select>
            </div>

            {/* Customer Info */}
            <div>
              <label className="block font-semibold text-zinc-700 mb-2">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newInvoice.customer_name}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, customer_name: e.target.value }))}
                placeholder="John Smith"
                data-testid="new-invoice-customer-name"
                className="w-full h-11 rounded-lg border-2 border-zinc-200 px-3 focus:border-[#207024]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newInvoice.customer_email}
                  onChange={(e) => setNewInvoice(prev => ({ ...prev, customer_email: e.target.value }))}
                  placeholder="john@email.com"
                  data-testid="new-invoice-email"
                  className="w-full h-10 rounded-lg border-2 border-zinc-200 px-3 text-sm focus:border-[#207024]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={newInvoice.customer_phone}
                  onChange={(e) => setNewInvoice(prev => ({ ...prev, customer_phone: e.target.value }))}
                  placeholder="(951) 555-1234"
                  data-testid="new-invoice-phone"
                  className="w-full h-10 rounded-lg border-2 border-zinc-200 px-3 text-sm focus:border-[#207024]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Address</label>
              <input
                type="text"
                value={newInvoice.customer_address}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, customer_address: e.target.value }))}
                placeholder="123 Main St, City, CA 92xxx"
                data-testid="new-invoice-address"
                className="w-full h-10 rounded-lg border-2 border-zinc-200 px-3 text-sm focus:border-[#207024]"
              />
            </div>

            {/* Quick Add Preset Items */}
            <div className="border-t pt-4">
              <div className="text-sm font-semibold text-zinc-700 mb-2">Quick Add Services</div>
              <div className="flex flex-wrap gap-2">
                {PRESET_LINE_ITEMS.filter(p => p.name !== "Custom Service").map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => addPresetLineItem(preset)}
                    type="button"
                    className="px-2 py-1 text-xs bg-zinc-100 hover:bg-zinc-200 rounded-full text-zinc-700"
                  >
                    + {preset.name} (${preset.unit_price})
                  </button>
                ))}
              </div>
            </div>

            {/* Line Items */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-zinc-700">Line Items</h4>
                <button
                  onClick={addLineItem}
                  type="button"
                  data-testid="add-line-item-button"
                  className="text-sm text-[#207024] hover:underline flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add Custom Item
                </button>
              </div>
              
              <div className="space-y-3">
                {newInvoice.line_items.map((item, index) => (
                  <div key={index} className="bg-zinc-50 rounded-lg p-3 relative">
                    {newInvoice.line_items.length > 1 && (
                      <button
                        onClick={() => removeLineItem(index)}
                        type="button"
                        className="absolute top-2 right-2 text-zinc-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <div className="mb-2">
                      <select
                        value={item.name}
                        onChange={(e) => {
                          if (e.target.value === "__custom__") {
                            updateLineItem(index, "name", "");
                          } else {
                            handlePresetSelect(index, e.target.value);
                          }
                        }}
                        className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                      >
                        <option value="">-- Select service --</option>
                        {PRESET_LINE_ITEMS.map(preset => (
                          <option key={preset.name} value={preset.name}>
                            {preset.name} - ${preset.unit_price}{preset.taxable ? " (taxable)" : ""}
                          </option>
                        ))}
                        <option value="__custom__">Custom item...</option>
                      </select>
                    </div>
                    {(item.name === "" || !PRESET_LINE_ITEMS.find(p => p.name === item.name)) && (
                      <div className="mb-2">
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateLineItem(index, "name", e.target.value)}
                          placeholder="Custom service description"
                          data-testid={`line-item-name-${index}`}
                          className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs text-zinc-500">Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, "quantity", parseInt(e.target.value) || 1)}
                          data-testid={`line-item-qty-${index}`}
                          className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500">Unit Price</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateLineItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                          data-testid={`line-item-price-${index}`}
                          className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.taxable}
                            onChange={(e) => updateLineItem(index, "taxable", e.target.checked)}
                            data-testid={`line-item-taxable-${index}`}
                            className="w-4 h-4 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                          />
                          <span className="text-zinc-600">Taxable</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals Preview */}
            {(() => {
              const totals = calculateNewInvoiceTotals();
              return totals.subtotal > 0 ? (
                <div className="border-t pt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Subtotal</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {totals.tax > 0 && (
                    <div className="flex justify-between text-zinc-500">
                      <span>Tax (8.75%)</span>
                      <span>{formatCurrency(totals.tax)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base border-t pt-2">
                    <span>Total</span>
                    <span className="text-[#207024]">{formatCurrency(totals.total)}</span>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Notes (optional)</label>
              <textarea
                value={newInvoice.notes}
                onChange={(e) => setNewInvoice(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                rows={2}
                data-testid="new-invoice-notes"
                className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2 text-sm focus:border-[#207024]"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setCreateDialogOpen(false);
                  setSelectedCustomerId("");
                  setNewInvoice({
                    customer_name: "",
                    customer_email: "",
                    customer_phone: "",
                    customer_address: "",
                    notes: "",
                    line_items: [{ name: "", quantity: 1, unit_price: 0, taxable: false }]
                  });
                }}
                type="button"
                className="flex-1 py-3 border-2 border-zinc-200 rounded-lg font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateInvoice}
                type="button"
                data-testid="submit-new-invoice-button"
                className="flex-1 py-3 bg-[#207024] text-white rounded-lg font-semibold"
              >
                <Plus className="w-5 h-5 inline mr-2" />
                Create Invoice
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Send Dialog */}
      <AlertDialog open={receiptDialogOpen} onOpenChange={(open) => {
        // Only allow closing if not actively sending
        if (!open && !sendingReceipt) {
          handleSendReceipt(false);
        }
      }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#207024]" />
              {isResendingReceipt ? "Resend Payment Receipt" : "Send Payment Receipt?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-left pt-2">
                <p className="text-zinc-700 mb-4">
                  How would you like to send the payment receipt to the customer?
                </p>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 rounded-lg border hover:bg-zinc-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={receiptSMS}
                      onChange={(e) => setReceiptSMS(e.target.checked)}
                      className="w-5 h-5 text-[#207024] rounded focus:ring-[#207024]"
                    />
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-zinc-500" />
                      <span className="text-zinc-700">Send via Text Message</span>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 rounded-lg border hover:bg-zinc-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={receiptEmail}
                      onChange={(e) => setReceiptEmail(e.target.checked)}
                      className="w-5 h-5 text-[#207024] rounded focus:ring-[#207024]"
                    />
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-zinc-500" />
                      <span className="text-zinc-700">Send via Email</span>
                    </div>
                  </label>
                </div>
                
                {!receiptSMS && !receiptEmail && (
                  <p className="text-amber-600 text-sm mt-2">
                    Select at least one delivery method
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel 
              onClick={() => handleSendReceipt(false)}
              disabled={sendingReceipt}
              className="border-zinc-300"
            >
              {isResendingReceipt ? "Cancel" : "Skip"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleSendReceipt(true);
              }}
              disabled={sendingReceipt || (!receiptSMS && !receiptEmail)}
              className="bg-[#207024] hover:bg-[#185a1c]"
            >
              {sendingReceipt ? "Sending..." : (isResendingReceipt ? "Resend Receipt" : "Send Receipt")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Review Request Dialog */}
      <AlertDialog open={reviewDialogOpen} onOpenChange={(open) => !open && handleSendReview(false)}>
        <AlertDialogContent className="max-w-md max-h-[90dvh] flex flex-col gap-0 p-0 overflow-hidden" data-testid="review-request-dialog">
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-6 pb-4" data-testid="review-dialog-scroll">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span className="text-2xl">⭐</span>
              Send Request for Review?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-left pt-2">
                <p className="text-zinc-700 mb-3">
                  Would you like to send this customer a text asking for a Google review?
                </p>
                
                {/* Tech Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Select Tech(s) to mention:
                  </label>
                  <div className="space-y-2">
                    {techs.map(tech => {
                      const idx = reviewTechNames.indexOf(tech.name);
                      const isChecked = idx !== -1;
                      const isPrimary = idx === 0;
                      return (
                        <label key={tech.tech_id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setReviewTechNames([...reviewTechNames, tech.name]);
                              } else {
                                setReviewTechNames(reviewTechNames.filter(n => n !== tech.name));
                              }
                            }}
                            className="w-4 h-4 text-[#207024] rounded focus:ring-[#207024]"
                          />
                          <span className="text-sm text-zinc-700">{tech.name}</span>
                          {isPrimary && reviewTechNames.length > 1 && (
                            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold uppercase tracking-wide">
                              Primary
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {reviewTechNames.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No tech selected - will say &quot;your Screen Tech&quot;</p>
                  )}
                  {reviewTechNames.length > 1 && (
                    <p className="text-xs text-zinc-500 mt-1">
                      Revenue is credited to the primary (first-checked) tech on Tech Performance.
                    </p>
                  )}
                </div>
                
                {/* Google Profile Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Google Business Profile:
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="reviewProfile"
                        value="main"
                        checked={reviewProfile === "main"}
                        onChange={() => setReviewProfile("main")}
                        className="w-4 h-4 text-[#207024] focus:ring-[#207024]"
                      />
                      <span className="text-sm text-zinc-700">Main Profile</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="reviewProfile"
                        value="secondary"
                        checked={reviewProfile === "secondary"}
                        onChange={() => setReviewProfile("secondary")}
                        className="w-4 h-4 text-[#207024] focus:ring-[#207024]"
                      />
                      <span className="text-sm text-zinc-700">Secondary Profile</span>
                    </label>
                  </div>
                </div>
                
                <div className="bg-zinc-50 rounded-lg p-3 text-sm text-zinc-600 border">
                  <p className="italic whitespace-pre-line">
{`"Thank you so much for choosing ScreenzZ to fix up your screens!

If you have a moment, would you mind leaving us a quick Google review? It truly helps our small business and makes it easier for other homeowners to find us.

As a thank you, when a review is left, we'll give your Screen ${reviewTechNames.length === 2 ? "Techs" : "Tech"}${reviewTechNames.length > 0 ? ` (${reviewTechNames.join(" and ")})` : ""} a $15 bonus on your behalf (no charge to you). We love recognizing our team for great service 😊

Here's the link if you'd like:
{review link}"`}
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          </div>
          <AlertDialogFooter className="flex flex-row gap-3 sm:gap-3 sm:space-x-0 border-t border-zinc-200 bg-white px-6 py-4 shrink-0">
            <AlertDialogCancel 
              onClick={() => handleSendReview(false)}
              disabled={sendingReview}
              className="flex-1 mt-0"
              data-testid="review-skip-btn"
            >
              No, Skip
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleSendReview(true)}
              disabled={sendingReview}
              className="flex-1 bg-[#207024] hover:bg-[#1a5c1d]"
              data-testid="review-send-btn"
            >
              {sendingReview ? "Sending..." : "Yes, Send"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-amber-600" />
              Edit Invoice {editInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Line Items */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">Line Items</label>
              {editLineItems.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2 items-start">
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => {
                      const updated = [...editLineItems];
                      updated[index].name = e.target.value;
                      setEditLineItems(updated);
                    }}
                    className="flex-1 h-10 rounded border border-zinc-200 px-2 text-sm"
                    placeholder="Service name"
                    data-testid={`edit-item-name-${index}`}
                  />
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => {
                      const updated = [...editLineItems];
                      updated[index].quantity = parseInt(e.target.value) || 1;
                      setEditLineItems(updated);
                    }}
                    className="w-16 h-10 rounded border border-zinc-200 px-2 text-sm text-center"
                    data-testid={`edit-item-qty-${index}`}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unit_price}
                    onChange={(e) => {
                      const updated = [...editLineItems];
                      updated[index].unit_price = parseFloat(e.target.value) || 0;
                      setEditLineItems(updated);
                    }}
                    className="w-24 h-10 rounded border border-zinc-200 px-2 text-sm"
                    placeholder="$0.00"
                    data-testid={`edit-item-price-${index}`}
                  />
                  <label className="flex items-center gap-1 text-xs text-zinc-500 mt-2.5">
                    <input
                      type="checkbox"
                      checked={item.taxable}
                      onChange={(e) => {
                        const updated = [...editLineItems];
                        updated[index].taxable = e.target.checked;
                        setEditLineItems(updated);
                      }}
                    />
                    Tax
                  </label>
                  {editLineItems.length > 1 && (
                    <button
                      onClick={() => setEditLineItems(editLineItems.filter((_, i) => i !== index))}
                      className="mt-2 text-red-400 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setEditLineItems([...editLineItems, { name: "", quantity: 1, unit_price: 0, taxable: false }])}
                className="text-sm text-[#207024] hover:underline mt-1"
                data-testid="edit-add-line-item"
              >
                + Add Line Item
              </button>
            </div>

            {/* Discount */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">Discount</label>
              {discountPresets.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="discount-presets">
                  {discountPresets.map((d) => (
                    <button key={d.item_id} type="button" data-testid={`discount-preset-${d.item_id}`}
                      onClick={() => setEditDiscount({ type: d.discount_type || "percent", value: d.discount_value || 0, description: d.name })}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${editDiscount.description === d.name ? "bg-[#207024] text-white border-[#207024]" : "bg-white text-zinc-700 border-zinc-300"}`}>
                      {d.name.replace(" Discount", "")} {d.discount_type === "percent" ? `${d.discount_value}%` : `$${d.discount_value}`}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <select
                  value={editDiscount.type}
                  onChange={(e) => setEditDiscount({ ...editDiscount, type: e.target.value })}
                  className="h-10 rounded border border-zinc-200 px-2 text-sm"
                  data-testid="edit-discount-type"
                >
                  <option value="">No discount</option>
                  <option value="dollar">$ Amount</option>
                  <option value="percent">% Percent</option>
                </select>
                {editDiscount.type && (
                  <div className="flex items-center gap-1">
                    {editDiscount.type === "dollar" && <span className="text-zinc-500">$</span>}
                    <input
                      type="number"
                      min="0"
                      step={editDiscount.type === "percent" ? "1" : "0.01"}
                      value={editDiscount.value || ""}
                      onChange={(e) => setEditDiscount({ ...editDiscount, value: parseFloat(e.target.value) || 0 })}
                      className="w-24 h-10 rounded border border-zinc-200 px-2 text-sm"
                      data-testid="edit-discount-value"
                    />
                    {editDiscount.type === "percent" && <span className="text-zinc-500">%</span>}
                  </div>
                )}
              </div>
              {editDiscount.type && (
                <input
                  type="text"
                  value={editDiscount.description}
                  onChange={(e) => setEditDiscount({ ...editDiscount, description: e.target.value })}
                  className="w-full h-9 mt-2 rounded border border-zinc-200 px-2 text-sm"
                  placeholder="Discount reason (e.g., Senior, Military)"
                  data-testid="edit-discount-description"
                />
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Notes</label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={2}
                className="w-full border border-zinc-200 rounded px-2 py-1.5 text-sm"
                placeholder="Optional notes"
                data-testid="edit-invoice-notes"
              />
            </div>

            {/* Totals Preview */}
            {(() => {
              const subtotal = editLineItems.reduce((sum, i) => sum + (i.quantity * i.unit_price), 0);
              let discountAmt = 0;
              if (editDiscount.type === "dollar") discountAmt = editDiscount.value || 0;
              else if (editDiscount.type === "percent") discountAmt = subtotal * (editDiscount.value || 0) / 100;
              const afterDiscount = subtotal - discountAmt;
              const taxable = editLineItems.filter(i => i.taxable).reduce((s, i) => s + (i.quantity * i.unit_price), 0);
              const taxRatio = subtotal > 0 ? taxable / subtotal : 0;
              const tax = afterDiscount * taxRatio * (editInvoice?.tax_rate || 0);
              const total = afterDiscount + tax;
              return (
                <div className="border-t pt-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  {discountAmt > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount{editDiscount.description && ` — ${editDiscount.description}`}</span>
                      <span>-{formatCurrency(discountAmt)}</span>
                    </div>
                  )}
                  {tax > 0 && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Tax</span>
                      <span>{formatCurrency(tax)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base pt-1 border-t">
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>
              );
            })()}

            {/* Save / Cancel */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setEditDialogOpen(false)}
                className="flex-1 h-12 rounded-xl border-2 border-zinc-200 text-zinc-700 font-semibold hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="flex-1 h-12 rounded-xl bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold disabled:opacity-50"
                data-testid="save-edit-invoice"
              >
                {savingEdit ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>



      <BottomNav />
    </div>
  );
}
